import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Dropdown, Modal, Popconfirm, Select } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'

import {
    CO7_INVITE_ROLES,
    CO7_MEMBER_ROLES,
    CO7_ROLE_LABELS,
    canEditCo7Collection,
    isCo7CollectionArchived,
} from '../co7.constants'
import {
    changeCo7MemberRole,
    clearCo7MembersModal,
    co7State,
    inviteCo7Member,
    loadCo7CollectionMembers,
    loadCo7Collections,
    loadCo7InviteUsers,
    removeCo7Member,
} from '../co7.slice'
import { CloseModalIcon, CrossIcon } from '../../../assets/icons'
import { showActionNotification } from '../../../components/notification'
import styles from './ManageMembersModal.module.css'

const getInitials = (name) => {
    if (!name) return 'U'
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return String(name).slice(0, 2).toUpperCase()
}

const getMemberColor = (memberName) => {
    const colors = [
        '#60a5fa',
        '#34d399',
        '#a78bfa',
        '#f472b6',
        '#fb923c',
        '#fbbf24',
        '#4ade80',
        '#22d3ee',
    ]
    if (!memberName) return colors[0]
    let hash = 0
    for (let i = 0; i < memberName.length; i++) {
        hash = memberName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
}

const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

const getUserProfilePicture = (user) =>
    user?.profilePicture || user?.profile || null

const renderInviteUserOption = (user) => {
    if (!user) return null
    const textLabel = user?.name || user?.email || 'Unnamed user'
    const profilePicture = getUserProfilePicture(user)

    return (
        <div className={styles.inviteUserOption}>
            {profilePicture ? (
                <Avatar size={28} src={profilePicture} />
            ) : (
                <Avatar
                    size={28}
                    style={{ backgroundColor: getMemberColor(textLabel) }}
                >
                    {getInitials(textLabel)}
                </Avatar>
            )}
            <div className={styles.inviteUserOptionMeta}>
                <span className={styles.inviteUserOptionName}>{textLabel}</span>
                {user?.email ? (
                    <span className={styles.inviteUserOptionEmail}>
                        {user.email}
                    </span>
                ) : null}
            </div>
        </div>
    )
}

const getRolePillClass = (role) => {
    if (role === CO7_MEMBER_ROLES.OWNER) return styles.rolePillOwner
    if (role === CO7_MEMBER_ROLES.EDITOR) return styles.rolePillEditor
    return styles.rolePillViewer
}

const ManageMembersModal = ({
    open,
    collection,
    currentUserId,
    isSuperAdmin,
    onClose,
}) => {
    const dispatch = useDispatch()
    const {
        inviteUsers,
        inviteUsersStatus,
        inviteUsersPage,
        inviteUsersSearch,
        inviteUsersIsNext,
        mutationStatus,
        activeMembers,
        membersStatus,
        membersError,
    } = useSelector(co7State)

    const [selectedUserIds, setSelectedUserIds] = useState([])
    const [selectedRole, setSelectedRole] = useState(CO7_MEMBER_ROLES.VIEWER)
    const [inviting, setInviting] = useState(false)
    const [updatingRoleUserId, setUpdatingRoleUserId] = useState(null)
    const [userSearchValue, setUserSearchValue] = useState('')
    const [inviteSelectedMap, setInviteSelectedMap] = useState({})

    const isArchived = isCo7CollectionArchived(collection)
    const canManageMembers = canEditCo7Collection(collection, isSuperAdmin)
    const members = activeMembers
    const isBusy = inviting || mutationStatus === 'loading'

    const reloadMembersData = async (
        collectionId,
        { refreshCollections = false } = {}
    ) => {
        if (!collectionId) return
        await dispatch(loadCo7CollectionMembers(collectionId))
        if (!isArchived) {
            await dispatch(
                loadCo7InviteUsers({
                    collectionId,
                    skip: 0,
                    search: '',
                })
            )
        }
        if (refreshCollections) {
            dispatch(loadCo7Collections({ reset: true }))
        }
    }

    useEffect(() => {
        if (open && collection?._id) {
            reloadMembersData(collection._id)
        }
        if (!open) {
            dispatch(clearCo7MembersModal())
            setSelectedUserIds([])
            setSelectedRole(CO7_MEMBER_ROLES.VIEWER)
            setUpdatingRoleUserId(null)
            setUserSearchValue('')
            setInviteSelectedMap({})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, collection?._id, dispatch])

    const handleInviteUserSearch = useMemo(
        () =>
            debounce((value) => {
                if (!collection?._id || isArchived) return
                dispatch(
                    loadCo7InviteUsers({
                        collectionId: collection._id,
                        skip: 0,
                        search: value,
                    })
                )
            }, 300),
        [collection?._id, dispatch, isArchived]
    )

    useEffect(
        () => () => handleInviteUserSearch.cancel(),
        [handleInviteUserSearch]
    )

    const handleInviteUserScroll = (e) => {
        if (!collection?._id || isArchived) return
        const { target } = e
        const scrollTop = target.scrollTop
        const scrollHeight = target.scrollHeight
        const clientHeight = target.clientHeight

        if (
            scrollHeight - scrollTop <= clientHeight + 50 &&
            inviteUsersIsNext &&
            inviteUsersStatus !== 'loading'
        ) {
            dispatch(
                loadCo7InviteUsers({
                    collectionId: collection._id,
                    skip: inviteUsers.length,
                    search: inviteUsersSearch,
                })
            )
        }
    }

    const userOptions = useMemo(
        () =>
            (inviteUsers || []).map((user) => ({
                value: user._id,
                label: user?.name || user?.email || 'Unnamed user',
                user,
                textLabel: user?.name || user?.email || 'Unnamed user',
                profilePicture: getUserProfilePicture(user),
            })),
        [inviteUsers]
    )

    const inviteUserMap = useMemo(() => {
        const map = { ...inviteSelectedMap }
        userOptions.forEach((option) => {
            map[option.value] = option
        })
        return map
    }, [userOptions, inviteSelectedMap])

    const handleInviteUserChange = (selectedValues, selectedOptions) => {
        // Clear search input only — do not refetch candidates on each selection
        setUserSearchValue('')
        setSelectedUserIds(selectedValues)

        const updatedMap = { ...inviteSelectedMap }
        if (selectedOptions?.length > 0) {
            selectedOptions.forEach((sel) => {
                if (!updatedMap[sel.value]) {
                    const match = userOptions.find(
                        (opt) => opt.value === sel.value
                    )
                    if (match) updatedMap[sel.value] = match
                }
            })
        } else {
            selectedValues.forEach((val) => {
                if (!updatedMap[val]) {
                    const match = userOptions.find((opt) => opt.value === val)
                    if (match) updatedMap[val] = match
                }
            })
        }

        Object.keys(updatedMap).forEach((key) => {
            if (!selectedValues.includes(key)) {
                delete updatedMap[key]
            }
        })
        setInviteSelectedMap(updatedMap)
    }

    const handleInvite = async () => {
        if (!selectedUserIds.length || !collection?._id || isArchived) return
        setInviting(true)
        try {
            const result = await dispatch(
                inviteCo7Member({
                    collectionId: collection._id,
                    userIds: selectedUserIds,
                    role: selectedRole,
                })
            )

            if (inviteCo7Member.fulfilled.match(result)) {
                const count = selectedUserIds.length
                showActionNotification({
                    type: 'success',
                    message:
                        count === 1
                            ? 'Member added successfully'
                            : `${count} members added successfully`,
                })
                setSelectedUserIds([])
                setSelectedRole(CO7_MEMBER_ROLES.VIEWER)
                setInviteSelectedMap({})
                setUserSearchValue('')
                await reloadMembersData(collection._id, {
                    refreshCollections: true,
                })
            } else {
                showActionNotification({
                    type: 'error',
                    message: result.payload || 'Failed to add members',
                })
            }
        } finally {
            setInviting(false)
        }
    }

    const handleRemove = async (userId) => {
        if (!collection?._id || isArchived) return
        const result = await dispatch(
            removeCo7Member({ collectionId: collection._id, userId })
        )
        if (removeCo7Member.fulfilled.match(result)) {
            showActionNotification({
                type: 'success',
                message: 'Member removed',
            })
            await reloadMembersData(collection._id, {
                refreshCollections: true,
            })
        } else {
            showActionNotification({
                type: 'error',
                message: result.payload || 'Failed to remove member',
            })
        }
    }

    const handleRoleChange = async (userId, role) => {
        if (!collection?._id || !userId || !role || isArchived) return
        setUpdatingRoleUserId(userId)
        try {
            const result = await dispatch(
                changeCo7MemberRole({
                    collectionId: collection._id,
                    userId,
                    role,
                })
            )
            if (changeCo7MemberRole.fulfilled.match(result)) {
                showActionNotification({
                    type: 'success',
                    message: 'Member role updated',
                })
                await reloadMembersData(collection._id, {
                    refreshCollections: true,
                })
            } else {
                showActionNotification({
                    type: 'error',
                    message: result.payload || 'Failed to update role',
                })
            }
        } finally {
            setUpdatingRoleUserId(null)
        }
    }

    return (
        <Modal
            title={
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderLeft}>
                        <div className={styles.modalHeaderIcon}>
                            <Icon
                                icon="mdi:account-group-outline"
                                width={20}
                                height={20}
                            />
                        </div>
                        <div>
                            <div className={styles.modalHeaderTitle}>
                                Members
                            </div>
                            <div className={styles.modalHeaderSubtitle}>
                                {collection?.name || 'Collection'}
                            </div>
                        </div>
                    </div>
                </div>
            }
            open={open}
            onCancel={onClose}
            footer={
                canManageMembers ? (
                    <div className={styles.modalFooter}>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                disabled={!selectedUserIds.length || isBusy}
                                onClick={handleInvite}
                                className={styles.submitButton}
                            >
                                {isBusy ? 'Adding...' : 'Add member & Save'}
                            </button>
                        </div>
                    </div>
                ) : null
            }
            width={580}
            destroyOnHidden
            centered
            className="co7-members-modal"
        >
            {canManageMembers ? (
                <div className={styles.inviteSection}>
                    <div className={styles.inviteSectionHeader}>
                        <Icon icon="mdi:account-plus-outline" width={20} />
                        <span>Invite member</span>
                    </div>
                    <div className={styles.inviteRow}>
                        <Select
                            mode="multiple"
                            value={selectedUserIds}
                            removeIcon={
                                <div style={{ height: '14px', width: '14px' }}>
                                    <CloseModalIcon />
                                </div>
                            }
                            placeholder="Select a user..."
                            size="small"
                            className={`inviteUserSelect ${styles.inviteUserSelect}`}
                            filterOption={false}
                            searchValue={userSearchValue}
                            onSearch={(value) => {
                                setUserSearchValue(value)
                                handleInviteUserSearch(value)
                            }}
                            autoClearSearchValue={false}
                            onPopupScroll={handleInviteUserScroll}
                            options={userOptions}
                            onChange={handleInviteUserChange}
                            loading={
                                inviteUsersStatus === 'loading' &&
                                inviteUsersPage === 1
                            }
                            suffixIcon={
                                <Icon
                                    icon="mdi:chevron-down"
                                    width={18}
                                    height={18}
                                />
                            }
                            optionRender={(option) =>
                                renderInviteUserOption(
                                    option.data?.user || option?.user
                                )
                            }
                            tagRender={(props) => {
                                const { closable, onClose, value: val } = props
                                const selectedUser = inviteUserMap[val]
                                const textLabel = selectedUser?.textLabel
                                const memberColor = getMemberColor(textLabel)
                                const initialsBgColor = hexToRgba(
                                    memberColor,
                                    0.3
                                )
                                const initials = getInitials(textLabel)

                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor:
                                                'rgba(147, 147, 147, 0.15)',
                                            borderRadius: 5,
                                            padding: '1px 5px 1px 1px',
                                            margin: 0,
                                            maxWidth: '100%',
                                            border: 'none',
                                            height: '24px',
                                            lineHeight: '22px',
                                        }}
                                    >
                                        {selectedUser?.profilePicture ? (
                                            <Avatar
                                                size={18}
                                                src={
                                                    selectedUser.profilePicture
                                                }
                                                style={{
                                                    marginRight: 5,
                                                    borderRadius: 3,
                                                }}
                                            />
                                        ) : (
                                            <div
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    borderRadius: 3,
                                                    backgroundColor:
                                                        initialsBgColor,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: 5,
                                                    fontSize: 9,
                                                    fontWeight: 600,
                                                    color: '#1e293b',
                                                }}
                                            >
                                                {initials}
                                            </div>
                                        )}
                                        <span
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 500,
                                                color: '#1e293b',
                                                marginRight: 3,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: 90,
                                                lineHeight: '18px',
                                            }}
                                        >
                                            {textLabel}
                                        </span>
                                        {closable && (
                                            <span
                                                onClick={onClose}
                                                style={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    marginLeft: 2,
                                                    padding: '1px',
                                                }}
                                            >
                                                <CrossIcon
                                                    width={8}
                                                    color="#64748b"
                                                />
                                            </span>
                                        )}
                                    </div>
                                )
                            }}
                            notFoundContent={
                                inviteUsersStatus === 'loading' &&
                                inviteUsersPage === 1 ? (
                                    <div className={styles.inviteUserLoading}>
                                        <Icon
                                            icon="line-md:loading-loop"
                                            style={{
                                                fontSize: 24,
                                                color: '#6366f1',
                                            }}
                                        />
                                    </div>
                                ) : (
                                    'No users available'
                                )
                            }
                            popupRender={(menu) => (
                                <>
                                    {menu}
                                    {inviteUsersStatus === 'loading' &&
                                        inviteUsersPage > 1 && (
                                            <div
                                                style={{
                                                    padding: '8px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Icon
                                                    icon="line-md:loading-loop"
                                                    style={{
                                                        fontSize: 24,
                                                        color: '#6366f1',
                                                    }}
                                                />
                                            </div>
                                        )}
                                </>
                            )}
                        />
                        <Select
                            className={`inviteRoleSelect ${styles.inviteRoleSelect}`}
                            value={selectedRole}
                            onChange={setSelectedRole}
                            suffixIcon={
                                <Icon
                                    icon="mdi:chevron-down"
                                    width={18}
                                    height={18}
                                />
                            }
                            options={CO7_INVITE_ROLES.map((r) => ({
                                value: r.value,
                                label: r.label,
                            }))}
                        />
                    </div>
                    <div className={styles.roleHintRow}>
                        {CO7_INVITE_ROLES.map((r) => (
                            <span key={r.value} className={styles.roleHint}>
                                <strong>{r.label}</strong> - {r.description}
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}

            <div className={styles.membersListSection}>
                <div className={styles.membersListHeader}>
                    {membersStatus === 'loading'
                        ? 'Loading members…'
                        : `${members?.length} member${members?.length === 1 ? '' : 's'}`}
                </div>

                {membersStatus === 'loading' ? (
                    <div className={styles.membersLoading}>
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 32, color: '#6366f1' }}
                        />
                    </div>
                ) : membersStatus === 'error' ? (
                    <div className={styles.membersError}>
                        <span>{membersError}</span>
                        <button
                            type="button"
                            className={styles.membersRetryBtn}
                            onClick={() => reloadMembersData(collection?._id)}
                        >
                            Try again
                        </button>
                    </div>
                ) : members?.length === 0 ? (
                    <div className={styles.membersEmpty}>
                        No members in this collection yet.
                    </div>
                ) : (
                    <ul className={styles.membersList}>
                        {members?.map((member) => (
                            <li
                                key={member?.userId}
                                className={styles.memberRow}
                            >
                                <div className={styles.memberRowLeft}>
                                    <Avatar
                                        size={36}
                                        src={member?.profilePicture}
                                        style={{ backgroundColor: '#6366f1' }}
                                    >
                                        {getInitials(member?.name)}
                                    </Avatar>
                                    <div>
                                        <div className={styles.memberName}>
                                            {member?.name}
                                            {member?.userId ===
                                            currentUserId ? (
                                                <span
                                                    className={styles.youBadge}
                                                >
                                                    You
                                                </span>
                                            ) : null}
                                        </div>
                                        {member?.email ? (
                                            <div className={styles.memberEmail}>
                                                {member?.email}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                <div className={styles.memberRowRight}>
                                    {canManageMembers &&
                                    member?.role !== CO7_MEMBER_ROLES.OWNER ? (
                                        <Dropdown
                                            trigger={['click']}
                                            disabled={
                                                updatingRoleUserId ===
                                                member?.userId
                                            }
                                            menu={{
                                                items: CO7_INVITE_ROLES.map(
                                                    (r) => ({
                                                        key: r.value,
                                                        label: r.label,
                                                    })
                                                ),
                                                selectedKeys: [member?.role],
                                                onClick: ({ key }) =>
                                                    handleRoleChange(
                                                        member?.userId,
                                                        key
                                                    ),
                                            }}
                                            getPopupContainer={(node) =>
                                                node?.parentElement ??
                                                document.body
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={`${styles.rolePill} ${styles.rolePillButton} ${getRolePillClass(member?.role)}`}
                                                disabled={
                                                    updatingRoleUserId ===
                                                    member?.userId
                                                }
                                                aria-label={`Change role for ${member?.name}`}
                                            >
                                                {updatingRoleUserId ===
                                                member?.userId ? (
                                                    <Icon
                                                        icon="line-md:loading-loop"
                                                        width={14}
                                                        height={14}
                                                    />
                                                ) : (
                                                    <>
                                                        {CO7_ROLE_LABELS[
                                                            member?.role
                                                        ] || member?.role}
                                                        <Icon
                                                            icon="mdi:chevron-down"
                                                            width={14}
                                                            height={14}
                                                            className={
                                                                styles.rolePillChevron
                                                            }
                                                        />
                                                    </>
                                                )}
                                            </button>
                                        </Dropdown>
                                    ) : (
                                        <span
                                            className={`${styles.rolePill} ${getRolePillClass(member?.role)}`}
                                        >
                                            {CO7_ROLE_LABELS[member?.role] ||
                                                member?.role}
                                        </span>
                                    )}
                                    {canManageMembers &&
                                    member?.role !== CO7_MEMBER_ROLES.OWNER ? (
                                        <Popconfirm
                                            title="Remove member?"
                                            description={`${member?.name || 'This member'} will lose access to this collection.`}
                                            okText="Remove"
                                            cancelText="Cancel"
                                            okButtonProps={{ danger: true }}
                                            placement="topRight"
                                            overlayClassName={
                                                styles.removeMemberPopconfirm
                                            }
                                            onConfirm={() =>
                                                handleRemove(member?.userId)
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={
                                                    styles.removeMemberBtn
                                                }
                                                aria-label={`Remove ${member?.name || 'member'}`}
                                            >
                                                <CrossIcon
                                                    width={10}
                                                    color="currentColor"
                                                />
                                            </button>
                                        </Popconfirm>
                                    ) : null}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Modal>
    )
}

export default ManageMembersModal
